import type { ComponentProps } from 'react'
import { colors } from '@ui/colors'
import Checkbox, { type CheckboxProps } from '@ui/forms/Checkbox'
import SectionRow from './SectionRow'
import { useTranslation } from 'react-i18next'

type CheckboxSectionRowStatus = 'default' | 'success' | 'danger'

type Props = Omit<
  ComponentProps<typeof SectionRow>,
  'icon' | 'iconComponent' | 'onPress' | 'required'
> & {
  value: boolean
  onChange: () => void
  onPress?: () => void
  required?: boolean
  status?: CheckboxSectionRowStatus
  checkboxProps?: Omit<CheckboxProps, 'value' | 'onChange' | 'noninteractive'>
}

export default function CheckboxSectionRow({
  value,
  onChange,
  onPress,
  required,
  status = 'default',
  checkboxProps,
  ...sectionRowProps
}: Props) {
  const isSuccess = status === 'success'
  const isDanger = status === 'danger'
  const colorActive = isSuccess
    ? colors.green[600]
    : isDanger
    ? colors.red[600]
    : colors.text
  const colorInactive = isDanger ? colors.red[600] : colors.text25

  const { t } = useTranslation(['ui'])

  return (
    <SectionRow
      {...sectionRowProps}
      info={required && isDanger ? t('ui:required') : sectionRowProps.info}
      onPress={onPress || onChange}
      required={required}
      variant={isSuccess ? 'success' : isDanger ? 'danger' : 'default'}
      iconComponent={
        <Checkbox
          value={value}
          onChange={onChange}
          noninteractive
          colorActive={colorActive}
          colorInactive={colorInactive}
          {...checkboxProps}
        />
      }
    />
  )
}
